import { Box } from '@deckee/deck-hand';
import React, { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { captureEvent } from '../../../../services/event-capture';
import { setSignalMemberFilter } from '../../../../services/map-layer/image-layers/signal-markers';
import { selectCurrentMemberId } from '../../../../services/store/modules/backend-service';
import { AppState } from '../../../../services/store';
import {
  fetchSignal,
  getAllSignalsFollowedByTheCurrentMember,
  getAllSignalsSharedWithCurrentMember,
  getSignal,
  setSelectedSignal,
} from '../../../../services/store/modules/signal';
import GroupDetailView, {
  GroupDetailMember,
} from '../components/GroupDetailView/GroupDetailView';
import {
  formatDistanceFromMember,
  sortByOnWaterThenDistance,
} from '../helpers/signal-sorting';
import { ReactSvg, deckeeLogo } from '../../../shared/vector-images';
import usePublicSignals from '../hooks/use-public-signals';
import useSignalUtilities from '../hooks/use-signal-utilities';

const SIGNAL_QUERY_PARAM = 'signal';

const IconWrapper = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
`;

const CommunityPage: FC = () => {
  const { pathname, search } = useLocation();
  const { publicSignals: allPublicSignals } = usePublicSignals();
  const followedSignals = useSelector(getAllSignalsFollowedByTheCurrentMember);
  const safetyContacts = useSelector(getAllSignalsSharedWithCurrentMember);
  const currentMemberId = useSelector(selectCurrentMemberId);
  const { centerMapOnSignal } = useSignalUtilities();
  const history = useHistory();
  const dispatch = useDispatch();

  const selectedSignalId =
    new URLSearchParams(search).get(SIGNAL_QUERY_PARAM) || undefined;

  const selectedSignal = useSelector((state: AppState) =>
    selectedSignalId ? getSignal(state, selectedSignalId) : undefined,
  );

  useEffect(() => {
    if (!selectedSignalId) {
      dispatch(setSelectedSignal(undefined));
      return;
    }
    dispatch(setSelectedSignal(selectedSignalId));
    const run = async () => {
      try {
        await dispatch(fetchSignal(selectedSignalId));
        await centerMapOnSignal(selectedSignalId);
      } catch {
        await centerMapOnSignal(selectedSignalId);
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSignalId]);

  // Community signals are followed + public signals, excluding safety contacts
  const communitySignals = [...followedSignals, ...allPublicSignals].filter(
    (signal) => !safetyContacts.find((sc) => sc.id === signal.id),
  );

  // Create stable member IDs string for dependency comparison
  const communityMemberIds = communitySignals.map((signal) => signal.memberid);
  const memberIdsKey = communityMemberIds.join(',');

  // Filter map signals to only show community members
  useEffect(() => {
    setSignalMemberFilter(communityMemberIds);

    // Clear filter when component unmounts
    return () => {
      setSignalMemberFilter(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberIdsKey]);

  const handleBack = () => {
    captureEvent({
      type: 'signal: back to groups list from community',
      payload: {},
      sendToAmplitude: true,
    });
    history.push('/maps/signal/groups');
  };

  const handleClose = () => {
    captureEvent({
      type: 'signal: close community page',
      payload: {},
      sendToAmplitude: true,
    });
    history.push('/maps');
  };

  const handleMemberClick = (signalId: string) => {
    captureEvent({
      type: 'signal: navigate to community signal',
      payload: {
        signalId,
      },
      sendToAmplitude: true,
    });
    if (!signalId) return;
    const isSameMember = selectedSignalId === signalId;
    if (isSameMember) {
      history.replace(pathname);
      dispatch(setSelectedSignal(undefined));
      return;
    }
    const params = new URLSearchParams(search);
    params.set(SIGNAL_QUERY_PARAM, signalId);
    history.replace(`${pathname}?${params.toString()}`);
    dispatch(setSelectedSignal(signalId));
    const run = async () => {
      try {
        await dispatch(fetchSignal(signalId));
        await centerMapOnSignal(signalId);
      } catch {
        await centerMapOnSignal(signalId);
      }
    };
    run();
  };

  // Transform community signals data for the presentation component
  const sortedSignals = sortByOnWaterThenDistance(communitySignals);

  // Move current user to top of list
  const currentUserIndex = sortedSignals.findIndex(
    (signal) => signal.memberid === currentMemberId,
  );
  if (currentUserIndex > 0) {
    const [currentUser] = sortedSignals.splice(currentUserIndex, 1);
    sortedSignals.unshift(currentUser);
  }

  const members: GroupDetailMember[] = sortedSignals.map((signal) => ({
    id: signal.id,
    memberId: signal.memberid,
    username: signal.username,
    signalId: signal.id,
    signalImage: signal.image || undefined,
    signalTimestamp: signal.timestamp,
    signalSubscribed: signal.subscribed,
    distanceFromMember: formatDistanceFromMember(signal.distance_from_member),
    isCurrentUser: signal.memberid === currentMemberId,
  }));

  return (
    <GroupDetailView
      isLoading={false}
      error={false}
      groupName="DECKEE Community"
      groupIcon={
        <IconWrapper>
          <ReactSvg
            toDisplay={deckeeLogo()}
            options={{ fillColor: 'white' }}
            width={28}
            height={28}
          />
        </IconWrapper>
      }
      memberCountLabel="10K+ members"
      noMembersMessage="No community members on the water"
      members={members}
      selectedSignalId={selectedSignalId}
      selectedSignal={selectedSignal ?? null}
      onBack={handleBack}
      onClose={handleClose}
      onMemberClick={handleMemberClick}
    />
  );
};

export default CommunityPage;
